/**
 * Attribution encoded into the Axon `source` field of a user-prompt event.
 *
 * The wire format is a pipe-separated triple, each segment optional from the
 * right:
 *
 * ```
 * reflex                     // server-authored, no human sender
 * reflex|usr_4HQJ…           // sent by that user, directly
 * reflex|usr_4HQJ…|chief     // sent by Chief on that user's behalf
 * ```
 *
 * `|` is the separator because prefixed base62 ids (`usr_<base62>`) already
 * contain `_`, and `|` is not legal in any of our id formats — so the split is
 * unambiguous. The source is durable: it is written to the Axon log, so every
 * viewer (and every later backfill) resolves the same sender rather than
 * attributing the prompt to whoever happens to be reading.
 *
 * This module is the single definition of that format. Producers (server
 * publish path, agent providers) build sources here; consumers (chat panes,
 * event bubbles) parse them here.
 */

import { z } from 'zod';

/** Source identifier for every Axon event published by the Reflex host. */
export const HOST_AXON_SOURCE = 'reflex';

/** Separator between the host source, the user id, and the via segment. */
export const HOST_AXON_SOURCE_SEPARATOR = '|';

/**
 * Who acted on the user's behalf. Absent means the user sent the prompt
 * themselves through a Reflex surface.
 */
export const PROMPT_VIA_VALUES = ['chief'] as const;
export type PromptVia = (typeof PROMPT_VIA_VALUES)[number];
export const PromptViaSchema = z.enum(PROMPT_VIA_VALUES);

/** How each relay is named in the UI. */
export const PROMPT_VIA_LABELS: Record<PromptVia, string> = { chief: 'Chief' };

export interface PromptAttribution {
  /** The human the prompt is attributed to; `null` when unattributed. */
  userId: string | null;
  /** The agent that sent it for them; `null` when they sent it themselves. */
  via: PromptVia | null;
}

/**
 * Encode prompt attribution as an Axon source. Without a user (server-driven
 * sends, user-less agents) this is the bare host source, matching connection
 * construction; `via` is only emitted alongside a user, because "on whose
 * behalf" is meaningless with no one to name.
 */
export function buildPromptSource(
  userId: string | null | undefined,
  via?: PromptVia | null,
): string {
  if (!userId) return HOST_AXON_SOURCE;
  const base = `${HOST_AXON_SOURCE}${HOST_AXON_SOURCE_SEPARATOR}${userId}`;
  return via ? `${base}${HOST_AXON_SOURCE_SEPARATOR}${via}` : base;
}

/**
 * Decode an Axon source into its attribution. Returns `null` for sources this
 * host did not author (`sdk`, `agent`, …) so callers can tell "not ours" from
 * "ours but unattributed" — the latter renders as "Unknown user".
 *
 * A `via` segment we don't recognize parses as `null` rather than failing the
 * whole source: an older client reading a newer sender's event still gets the
 * user right, which is the part that matters.
 */
export function parsePromptSource(source: string | null | undefined): PromptAttribution | null {
  if (!source) return null;
  const [host, userId, via] = source.split(HOST_AXON_SOURCE_SEPARATOR);
  if (host !== HOST_AXON_SOURCE) return null;
  const parsedVia = PromptViaSchema.safeParse(via);
  return {
    userId: userId ? userId : null,
    via: parsedVia.success ? parsedVia.data : null,
  };
}

/** True when `source` already names a sender — see `parsePromptSource`. */
export function isAttributedPromptSource(source: string | null | undefined): boolean {
  return parsePromptSource(source)?.userId != null;
}
